import { Autocomplete } from "@base-ui/react/autocomplete";
import { Dialog } from "@base-ui/react/dialog";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowDownIcon, ArrowUpIcon, EnterIcon, SearchIcon } from "../icons.ts";
import { darkTheme } from "../themes.ts";
import "../tokens.stylex.ts";
import { color, font, fontSize, fontWeight, media, motion, radius, space, zIndex } from "../tokens.stylex.ts";
import { Kbd } from "./Kbd.tsx";
import { isTransitioning } from "./overlay.styles.ts";
import { surface } from "./surface.styles.ts";
export interface CommandItem {
  /** Stable, unique across all groups. */
  id: string;
  label: string;
  icon?: ReactNode;
  /** Extra search terms that aren't displayed: synonyms, slugs, the parent object's name. */
  keywords?: readonly string[];
  /** Right-aligned hint, e.g. a shortcut or "Current". */
  hint?: ReactNode;
  onSelect: () => void;
}
export interface CommandGroup {
  label: string;
  items: readonly CommandItem[];
}
export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: readonly CommandGroup[];
  /** Item ids to surface in a "Recent" group while the query is empty, most recent first. */
  recentIds?: readonly string[];
  /** Called after any item runs, e.g. to record it as recent. */
  onItemSelect?: (item: CommandItem) => void;
  placeholder?: string;
}
interface ListGroup {
  label: string;
  items: CommandItem[];
}
const RECENT_PREFIX = "recent:";

// `height: auto` can't be animated in every browser yet, so PaletteBody measures
// the results on mount and hands the target over as `--body-height`.
const expand = "xj7did-B";

/**
 * ⌘K-style launcher: a modal search box over grouped commands.
 *
 * Built on Base UI's Autocomplete rendered `inline` inside a Dialog, so the
 * input owns focus while ↑/↓ move a virtual highlight through the list
 * (`aria-activedescendant`), ↵ runs it and Esc closes. Matching is
 * accent- and case-insensitive; every word of the query must appear in the
 * label, keywords or group name, in any order ("key api" finds "API keys").
 * The popup always renders in the dark theme, whatever the page uses.
 *
 * Opening is two beats: the search box fades in on its own, then the results
 * grow down beneath it to their natural height.
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  recentIds,
  onItemSelect,
  placeholder = "Type a command or search…"
}: CommandPaletteProps) {
  return <Dialog.Root open={open} onOpenChange={next => onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Backdrop className={state => ({
        0: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni x7updtu"
        },
        1: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni x7updtu xg01cxk"
        }
      })[!!isTransitioning(state.transitionStatus) << 0].className} />
        <Dialog.Popup aria-label="Command palette" className={state => stylex.props(
      // Always dark: the theme's variable overrides on the popup cascade to everything inside.
      ...darkTheme, surface.raised, surface.lg, surface.plain, styles.popup, isTransitioning(state.transitionStatus) && styles.popupHidden).className}>
          {/* Mounted only while open, so the query and highlight reset on every open. */}
          <PaletteBody groups={groups} recentIds={recentIds} placeholder={placeholder} onRun={item => {
          onOpenChange(false);
          item.onSelect();
          onItemSelect?.(item);
        }} />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>;
}
function PaletteBody({
  groups,
  recentIds,
  placeholder,
  onRun
}: {
  groups: readonly CommandGroup[];
  recentIds: readonly string[] | undefined;
  placeholder: string;
  onRun: (item: CommandItem) => void;
}) {
  const [query, setQuery] = useState("");
  const {
    contains
  } = Autocomplete.useFilter({
    sensitivity: "base"
  });
  const bodyRef = useRef<HTMLDivElement>(null);

  // Mounted on every open, so this measures the results each time the palette opens.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    body?.style.setProperty("--body-height", `${body.scrollHeight}px`);
  }, []);
  const items = useMemo<ListGroup[]>(() => {
    const list = groups.filter(group => group.items.length > 0).map(group => ({
      ...group,
      items: [...group.items]
    }));
    if (query.trim() || !recentIds?.length) return list;
    const byId = new Map(list.flatMap(group => group.items.map(item => [item.id, item] as const)));
    const recent = recentIds.flatMap(id => {
      const item = byId.get(id);
      // A distinct id keeps the copy apart from the original in the list.
      return item ? [{
        ...item,
        id: `${RECENT_PREFIX}${item.id}`
      }] : [];
    });
    return recent.length ? [{
      label: "Recent",
      items: recent
    }, ...list] : list;
  }, [groups, recentIds, query]);
  const searchText = useMemo(() => {
    const text = new Map<string, string>();
    for (const group of groups) {
      for (const item of group.items) {
        text.set(item.id, [item.label, group.label, ...(item.keywords ?? [])].join(" "));
      }
    }
    return text;
  }, [groups]);
  return <Autocomplete.Root items={items} value={query} onValueChange={setQuery} itemToStringValue={(item: CommandItem) => item.label} filter={(item: CommandItem, rawQuery) => {
    const haystack = searchText.get(item.id) ?? item.label;
    return rawQuery.trim().split(/\s+/).every(word => contains(haystack, word));
  }} inline open autoHighlight="always" keepHighlight>
      <div className="x78zum5 x6s0dn4 x1a62zme xws8vp0 xso031l x1q0q8m5 xxsbevz">
        <SearchIcon size={16} className="x2lah0s xxe3v3u" aria-hidden />
        <Autocomplete.Input placeholder={placeholder} aria-label="Search commands" spellCheck={false} autoComplete="off" className="x1iyjqo2 xeuugli xdd8jsf x1717udv xc342km x1a2a7pz xjbqb8w x1wv6yj x1p23z6u x9aqtqa xm46rn6" />
        <span className="x78zum5 xeca93d">
          <Kbd>Esc</Kbd>
        </span>
      </div>

      <div ref={bodyRef} className="x2lah0s x7giv3 x5rcijh xfcbtqh x3nmhug x1aquc0h x1g18oiy x15e7naz xxn97zd xskzprw">
        <Autocomplete.Empty className="x1ja3kyt xws8vp0 x2b8uid xluwven xxe3v3u xjl7jj">
          {query.trim() ? `No results for “${query.trim()}”` : "Nothing to show"}
        </Autocomplete.Empty>

        <Autocomplete.List className="xwqlhrm xy4mbk0 x1odjw0f xish69e x13hyyd1 x1kpc7t0 x1a2a7pz xjl7jj">
          {(group: ListGroup) => <Autocomplete.Group key={group.label} items={group.items} className="x16h6cv7">
              <Autocomplete.GroupLabel className="x1uu5im9 xs5ehr0 x18dy136 xxg9qwl xmccgnw xjat59b xxe3v3u">{group.label}</Autocomplete.GroupLabel>
              <Autocomplete.Collection>
                {(item: CommandItem) => <Autocomplete.Item key={item.id} value={item} onClick={() => onRun(item)} className={state => ({
              0: {
                className: "x78zum5 x6s0dn4 xpcbtbh x16wdlz0 xcpwuvp x1uu5im9 xoazux2 xluwven xcb8g08 x187ovcu x1ypdohk x1a2a7pz x87ps6o x1qycbc0"
              },
              1: {
                className: "x78zum5 x6s0dn4 xpcbtbh x16wdlz0 xcpwuvp x1uu5im9 xoazux2 xluwven xcb8g08 x1ypdohk x1a2a7pz x87ps6o x1qycbc0 xqannp5 x9aqtqa"
              }
            })[!!state.highlighted << 0].className}>
                    {item.icon ? <span className="x78zum5 x2lah0s x1kky2od xl56j7k xxe3v3u">{item.icon}</span> : null}
                    <span className="x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft x1219144">{item.label}</span>
                    {item.hint ? <span className="x2lah0s x78zum5 x6s0dn4 x92x8s6 xmccgnw xxe3v3u">{item.hint}</span> : null}
                  </Autocomplete.Item>}
              </Autocomplete.Collection>
            </Autocomplete.Group>}
        </Autocomplete.List>

        <div className="x4l6xbm x1imsgqj xws8vp0 x178xt8z x13fuv20 x8yrp0t x5rcijh xfcbtqh xvccuex x78zum5 xeca93d" aria-hidden>
          <span className="x78zum5 x6s0dn4 xjenv9e xmccgnw xxe3v3u">
            <Kbd>
              <ArrowUpIcon size={11} />
            </Kbd>
            <Kbd>
              <ArrowDownIcon size={11} />
            </Kbd>
            Navigate
          </span>
          <span className="x78zum5 x6s0dn4 xjenv9e xmccgnw xxe3v3u">
            <Kbd>
              <EnterIcon size={11} />
            </Kbd>
            Open
          </span>
        </div>
      </div>
    </Autocomplete.Root>;
}
const styles = {
  popup: {
    kVAEAm: "xixxii4",
    k87sOh: "x38wyzf xsyyyt5",
    kbCHJM: "x1nrll8i",
    k3aq6I: "xuuh30",
    kY2c9j: "xlj7wz5",
    kzqmXN: "x1cf4oau x1en9ctu",
    ks0D6T: "xrlsmeg",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kaIpWk: "x16l8lxw",
    kI3sdo: "x1a2a7pz",
    k1ekBW: "x6o7n8i",
    kIyJzY: "x7updtu",
    kAMwcw: "xyda15a",
    $$css: true
  },
  popupHidden: {
    kSiTet: "xg01cxk",
    k3aq6I: "x19w7evf",
    $$css: true
  }
};